import React, { useState } from 'react';
import { PlanningPageType } from './PlanningPage';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import RoleTag from './widgets/RoleTag';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import { PratusProgressBar } from 'components/DesignSystems/New/PratusProgressBar';
import { useIncidentInfo, useUpdateIcsInfo } from '../hooks/incidentInfoHooks';
import { ConditionalCollapsibleCard } from '../ConditionalCollapsibleCard';
import { getIncidentCardTitle, imhMeetingGroundRules } from './USCGPage';

type PlanningAgendaProps = {
  planningPhases: PlanningPageType[];
  currentPhase: number;
};

export const PlanningAgenda = ({
  planningPhases,
  currentPhase,
}: PlanningAgendaProps) => {
  const agenda = planningPhases[currentPhase].agenda;
  const agendaItemCount = agenda.length;

  const { data: incidentInfo } = useIncidentInfo();
  const mutate = useUpdateIcsInfo();

  const notes: string[] =
    incidentInfo?.agendaItems?.[currentPhase]?.notes ||
    Array(agendaItemCount).fill('');

  const checked: string[] =
    incidentInfo?.agendaItems?.[currentPhase]?.checked ||
    Array(agendaItemCount).fill('');

  const checkedCount = checked.filter(Boolean).length;

  const roleCounts: Record<string, number> = {};
  agenda.forEach((item) => {
    roleCounts[item.role] = (roleCounts[item.role] || 0) + 1;
  });

  const checkedRoleCounts: Record<string, number> = {};
  agenda.forEach((item, idx) => {
    if (checked[idx]) {
      checkedRoleCounts[item.role] = (checkedRoleCounts[item.role] || 0) + 1;
    }
  });

  const handleCheck = async (index: number, item: { role: string }) => {
    const updatedChecked = [...checked];
    if (updatedChecked[index] === '') {
      updatedChecked[index] = item.role;
    } else {
      updatedChecked[index] = '';
    }
    await mutate.mutateAsync({
      ...incidentInfo,
      agendaItems: {
        ...incidentInfo?.agendaItems,
        [currentPhase]: {
          ...(incidentInfo?.agendaItems &&
            incidentInfo?.agendaItems[currentPhase]),
          checked: updatedChecked,
        },
      },
    });
  };

  const handleSave = async (value: string, index: number) => {
    const updatedNotes = [...notes];
    updatedNotes[index] = value;
    await mutate.mutateAsync({
      ...incidentInfo,
      agendaItems: {
        ...incidentInfo?.agendaItems,
        [currentPhase]: {
          ...(incidentInfo?.agendaItems &&
            incidentInfo?.agendaItems[currentPhase]),
          notes: updatedNotes,
        },
      },
    });
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey="1"
      headerLeft={
        <div className="d-flex flex-column align-items-start">
          <h3 className="mb-0 mt-0">
            <SharedIcon
              iconName="check_circle"
              classes="me-2"
              color="primary"
            />
            {getIncidentCardTitle('agenda')}
          </h3>
        </div>
      }
      className="planning-widget"
    >
      <div className="bordered-card p-3 m-3 bg-bglight">
        <PratusProgressBar
          current={checkedCount}
          totalNum={agendaItemCount}
          header="Overall Progress"
        />
        <div className="row mt-1">
          {Object.keys(roleCounts).map((role) => {
            return (
              <span key={role} className="mb-2 col-6">
                <PratusProgressBar
                  current={checkedRoleCounts[role] || 0}
                  totalNum={roleCounts[role]}
                  header={<RoleTag icsRoles={[role]} />}
                  className="mt-1"
                />
              </span>
            );
          })}
        </div>
      </div>
      <div className="px-4 pb-2 mb-3">
        <div className="fw-semibold small text-secondary mb-1">
          Ground Rules
        </div>
        <ul className="mb-0 ps-3">
          {imhMeetingGroundRules.map((rule) => (
            <li key={rule}>
              <div style={{ wordBreak: 'break-word' }}>{rule}</div>
            </li>
          ))}
        </ul>
      </div>
      <div className="px-4 pb-2">
        <div className="fw-semibold small text-secondary mb-2">Agenda</div>
        <div className="p-2 pt-0">
          {agenda.map((item, index) => (
            <div
              key={index}
              className="d-flex justify-content-between align-items-center p-2"
            >
              <AgendaEntry
                key={`${currentPhase}-${index}-${item.item}`}
                text={item.item}
                role={item.role}
                checked={checked[index] !== ''}
                onCheck={() => handleCheck(index, item)}
                handleSave={(value) => handleSave(value, index)}
                note={notes[index]}
                index={index}
              />
            </div>
          ))}
        </div>
      </div>
    </ConditionalCollapsibleCard>
  );
};

const AgendaEntry = ({
  text,
  checked,
  onCheck,
  role,
  handleSave,
  note,
  index,
}: {
  text: string;
  checked: boolean;
  onCheck: () => void;
  role: string;
  handleSave: (value: string) => Promise<void>;
  note?: string;
  index: number;
}) => {
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center w-100 m-2">
        <div
          role="button"
          className="d-flex align-items-center "
          onClick={onCheck}
        >
          <StylishNewCheckbox
            id={`${index}-agenda-item`}
            checked={checked}
            onChange={onCheck}
            bordered
          />
          <RoleTag icsRoles={[role]} />
          {saving ? (
            <span className="ms-1">Saving...</span>
          ) : (
            <span className="ms-1" style={{ userSelect: 'none' }}>
              {text}
            </span>
          )}
        </div>
        <StylishNewButton
          className="button button--link ms-2"
          onClick={() => setShowEdit(!showEdit)}
        >
          <SharedIcon iconName="edit" color="light" />
        </StylishNewButton>
      </div>
      {showEdit ? (
        <PratusTextArea
          rows={1}
          placeholder="Add Notes..."
          defaultValue={note}
          onSave={async (value) => {
            setSaving(true);
            await handleSave(value);
            setShowEdit(false);
            setSaving(false);
          }}
          className="ms-5 w-90"
        />
      ) : (
        note && (
          <div className="ms-5">
            <ul>
              <li>
                <div style={{ wordBreak: 'break-word' }}>{note}</div>
              </li>
            </ul>
          </div>
        )
      )}
    </div>
  );
};
